/**
 * useKeyboardShortcuts.ts — Hook global de atajos de teclado
 *
 * QUÉ HACE:
 *  - Registra atajos de teclado de forma centralizada y los limpia al desmontar.
 *  - Soporta modificadores: Ctrl/Cmd, Shift, Alt.
 *  - En web y desktop funciona igual; en móvil los atajos se omiten (no hay teclado físico).
 *  - Previene conflictos con inputs/textareas activos (ignora eventos en campos editables).
 *
 * QUÉ EXPORTA: useKeyboardShortcuts (hook), useGlobalEscKey (atajo simple para modales)
 *
 * QUÉ NO DEBE HACER:
 *  - No contener lógica de negocio
 *  - No hacer llamadas API
 *  - No importar componentes
 *
 * EJEMPLO DE USO:
 *  useKeyboardShortcuts([
 *    { key: "Escape", handler: onClose },
 *    { key: "Enter", handler: onConfirm },
 *    { key: "n", ctrl: true, handler: onNewSale, description: "Nueva venta" },
 *  ]);
 *
 *  // Atajo simple para cerrar cualquier modal:
 *  useGlobalEscKey(onClose, isOpen);
 */

import { useEffect } from "react";
import { isNative } from "@/lib/platform";

// ── Tipos ─────────────────────────────────────────────────────────────────────

export interface KeyboardShortcut {
  /** Tecla principal (case-insensitive). Ej: "Escape", "Enter", "n", "F1" */
  key: string;
  /** Requiere Ctrl (Windows/Linux) o Cmd (macOS) */
  ctrl?: boolean;
  /** Requiere Shift */
  shift?: boolean;
  /** Requiere Alt */
  alt?: boolean;
  /** Ejecutar incluso dentro de inputs (default: false — se ignora en campos) */
  allowInInputs?: boolean;
  /** Descripción para documentación / accesibilidad */
  description?: string;
  /** Función a ejecutar */
  handler: (event: KeyboardEvent) => void;
}

// ── Utilidad: ¿el foco está en un campo editable? ─────────────────────────────

function isEditableTarget(target: EventTarget | null): boolean {
  if (!target) return false;
  const el = target as HTMLElement;
  const tag = el.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (el.isContentEditable) return true;
  return false;
}

// ── Hook principal ─────────────────────────────────────────────────────────────

/**
 * Registra una lista de atajos de teclado mientras el componente esté montado.
 * @param shortcuts Lista de atajos a registrar
 * @param enabled   Si `false`, los atajos se desactivan sin desmontar (útil para modales apilados)
 */
export function useKeyboardShortcuts(
  shortcuts: KeyboardShortcut[],
  enabled = true,
): void {
  useEffect(() => {
    // En móvil nativo no hay teclado físico permanente → no registrar
    if (isNative || !enabled || shortcuts.length === 0) return;

    const handler = (event: KeyboardEvent): void => {
      for (const shortcut of shortcuts) {
        const keyMatch = event.key.toLowerCase() === shortcut.key.toLowerCase();
        const ctrlMatch = shortcut.ctrl
          ? event.ctrlKey || event.metaKey
          : !event.ctrlKey && !event.metaKey;
        const shiftMatch = shortcut.shift ? event.shiftKey : !event.shiftKey;
        const altMatch = shortcut.alt ? event.altKey : !event.altKey;

        if (!keyMatch || !ctrlMatch || !shiftMatch || !altMatch) continue;

        // Omitir si el foco está en un campo editable (a menos que el atajo lo permita)
        if (!shortcut.allowInInputs && isEditableTarget(event.target)) continue;

        event.preventDefault();
        shortcut.handler(event);
        // Solo ejecutar el primer atajo que coincida
        break;
      }
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [shortcuts, enabled]);
}

// ── Atajo simplificado: Escape para cerrar modales ────────────────────────────

/**
 * Registra únicamente la tecla Escape cuando el modal/overlay esté abierto.
 * @param onClose Función de cierre
 * @param isOpen  Solo activa el listener cuando sea `true`
 */
export function useGlobalEscKey(onClose: () => void, isOpen: boolean): void {
  useEffect(() => {
    if (!isOpen || isNative) return;

    const handler = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);
}

// ── Atajo simplificado: Enter para confirmar ──────────────────────────────────

/**
 * Registra Enter (sin modificadores) para confirmar una acción.
 * Ignora si el foco está en un textarea (Enter = salto de línea) o submit de form.
 * @param onConfirm Función a ejecutar
 * @param enabled   Activa solo cuando sea `true`
 */
export function useGlobalEnterKey(
  onConfirm: () => void,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled || isNative) return;

    const handler = (event: KeyboardEvent): void => {
      if (event.key !== "Enter") return;
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey)
        return;

      const target = event.target as HTMLElement;
      // Permitir Enter normal en textarea y buttons de tipo submit
      if (target.tagName.toLowerCase() === "textarea") return;
      if (
        target.tagName.toLowerCase() === "button" &&
        (target as HTMLButtonElement).type === "submit"
      )
        return;

      event.preventDefault();
      onConfirm();
    };

    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [enabled, onConfirm]);
}
